import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Flex, HStack, Input, Kbd, Modal, ModalBody, ModalContent, ModalOverlay,
  Spinner, Text, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiFolder, FiFileText, FiDollarSign, FiTool, FiShoppingCart, FiClipboard,
  FiUser, FiUsers, FiPackage, FiSearch,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import useGlobalSearch from '../hooks/useGlobalSearch';
import { SearchHit, SearchHitType } from '../models/searchHit';
import { useAppearance } from '../theme/AppearanceProvider';

// item 107: ⌘K entire-site search. Self-contained — listens for ⌘K/Ctrl-K and a
// `basix:open-search` window event (so the top-bar button can open it too), then
// shows grouped, deep-linked results from the cross-entity /api/search endpoint.

// the open event the top-bar search button dispatches
export const OPEN_SEARCH_EVENT = 'basix:open-search';

// group order + display, plus the route each type deep-links to
const TYPE_META: Record<SearchHitType, { label: string; icon: React.ReactNode; route: (id: string) => string }> = {
  Project: { label: 'Projects', icon: <FiFolder />, route: (id) => `/project/${id}` },
  Proposal: { label: 'Proposals', icon: <FiFileText />, route: (id) => `/proposal/${id}` },
  Invoice: { label: 'Invoices', icon: <FiDollarSign />, route: (id) => `/invoice/${id}` },
  WorkOrder: { label: 'Work Orders', icon: <FiTool />, route: (id) => `/WorkOrder/${id}` },
  SalesOrder: { label: 'Sales Orders', icon: <FiTool />, route: (id) => `/WorkOrder/${id}` },
  PurchaseOrder: { label: 'Purchase Orders', icon: <FiShoppingCart />, route: (id) => `/PurchaseOrder/${id}` },
  Bill: { label: 'Bills', icon: <FiClipboard />, route: (id) => `/bill/${id}` },
  Customer: { label: 'CRM', icon: <FiUser />, route: (id) => `/customer/${id}` },
  Item: { label: 'Items', icon: <FiPackage />, route: (id) => `/item/${id}` },
  // item 196 — team directory in global search; deep-links to the user record
  User: { label: 'Users', icon: <FiUsers />, route: (id) => `/user/${id}` },
};

const GROUP_ORDER: SearchHitType[] = [
  'Project', 'Proposal', 'Invoice', 'WorkOrder', 'SalesOrder', 'PurchaseOrder', 'Bill', 'Customer', 'Item', 'User',
];

const CommandPalette: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const history = useHistory();
  const { aurora } = useAppearance();

  const { hits, isLoading } = useGlobalSearch(isOpen ? query : '');

  const panelBg = useColorModeValue(
    aurora ? 'rgba(255,255,255,0.82)' : 'white',
    aurora ? 'rgba(20,24,33,0.92)' : 'gray.800',
  );
  const border = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const groupColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const activeBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => { setIsOpen(false); setQuery(''); setActive(0); }, []);

  // ⌘K / Ctrl-K toggles; the top-bar button fires basix:open-search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, open);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_SEARCH_EVENT, open);
    };
  }, [open]);

  // ordered flat list (group order) for keyboard navigation + the grouped render
  const grouped = useMemo(() => {
    const byType = GROUP_ORDER
      .map((type) => ({ type, items: hits.filter((h) => h.type === type) }))
      .filter((g) => g.items.length > 0);
    const flat: SearchHit[] = byType.flatMap((g) => g.items);
    return { byType, flat };
  }, [hits]);

  useEffect(() => { setActive(0); }, [query]);

  const go = useCallback((hit: SearchHit) => {
    history.push(TYPE_META[hit.type].route(hit.id));
    close();
  }, [history, close]);

  const onInputKey = (e: React.KeyboardEvent) => {
    const { flat } = grouped;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flat[active]) {
      e.preventDefault();
      go(flat[active]);
    }
  };

  // running index across groups so the active highlight maps to the flat list
  let runningIndex = -1;

  return (
    <Modal isOpen={isOpen} onClose={close} size="xl" scrollBehavior="inside" isCentered>
      <ModalOverlay backdropFilter="var(--bx-glass-veil)" />
      <ModalContent
        bg={panelBg}
        borderWidth="1px"
        borderColor={border}
        borderRadius="2xl"
        overflow="hidden"
        sx={aurora ? { backdropFilter: 'var(--bx-glass-panel)' } : undefined}
        mt={{ base: 16, md: 24 }}
      >
        <Flex align="center" px={4} py={3} borderBottomWidth="1px" borderColor={border}>
          {/* item 177 — explicit mr (Chakra-1.6 gap unreliable) so the icon clears the input */}
          <Box color={subColor} fontSize="18px" mr={3}><FiSearch /></Box>
          <Input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            variant="unstyled"
            placeholder="Search projects, documents, customers, items…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            fontSize="md"
          />
          {isLoading && <Spinner size="sm" color={subColor} />}
          <Kbd>esc</Kbd>
        </Flex>
        <ModalBody px={2} py={2} maxH="60vh">
          {query.trim().length < 2 && (
            <Text px={3} py={6} color={subColor} fontSize="sm" textAlign="center">
              Type at least 2 characters to search the whole app.
            </Text>
          )}
          {query.trim().length >= 2 && !isLoading && grouped.flat.length === 0 && (
            <Text px={3} py={6} color={subColor} fontSize="sm" textAlign="center">
              {`No results for “${query.trim()}”.`}
            </Text>
          )}
          {grouped.byType.map((g) => (
            <Box key={g.type} mb={1}>
              <Text px={3} pt={2} pb={1} fontSize="xs" fontWeight="bold" letterSpacing="0.06em" textTransform="uppercase" color={groupColor}>
                {TYPE_META[g.type].label}
              </Text>
              {g.items.map((hit) => {
                runningIndex += 1;
                const idx = runningIndex;
                return (
                  <HStack
                    key={`${hit.type}-${hit.id}`}
                    px={3}
                    py={2}
                    spacing={3}
                    borderRadius="lg"
                    cursor="pointer"
                    bg={idx === active ? activeBg : undefined}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => go(hit)}
                  >
                    <Box color={groupColor} fontSize="16px" flexShrink={0}>{TYPE_META[hit.type].icon}</Box>
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{hit.title}</Text>
                      {hit.subtitle && <Text fontSize="xs" color={subColor} noOfLines={1}>{hit.subtitle}</Text>}
                    </Box>
                  </HStack>
                );
              })}
            </Box>
          ))}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CommandPalette;
